/**
 * WidgetState — standardized loading / empty / error states.
 *
 * Replaces ad-hoc spinners and "No data" divs scattered across renderers
 * and section components. Use as the inner content when data is loading,
 * the result set is empty, or the fetch errored. Styled to match the
 * widget-card chrome and respects light/dark via Tailwind tokens.
 *
 * Phase E4 of issue #90 v3 plan.
 */
import { AlertCircle, Inbox } from 'lucide-react'

interface Props {
  state: 'loading' | 'empty' | 'error'
  /** Optional override message; defaults to a sensible per-state phrase. */
  message?: string
  /** Optional retry callback for the error state. */
  onRetry?: () => void
}

export default function WidgetState({ state, message, onRetry }: Props) {
  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" aria-label="Loading" />
      </div>
    )
  }

  if (state === 'empty') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 px-4">
        <Inbox size={28} className="opacity-50 mb-2" />
        <p className="text-xs text-center">{message || 'No data'}</p>
      </div>
    )
  }

  // state === 'error'
  return (
    <div className="flex flex-col items-center justify-center h-full text-red-500 dark:text-red-400 px-4">
      <AlertCircle size={28} className="opacity-80 mb-2" />
      <p className="text-xs text-center">{message || 'Could not load this widget.'}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-2 text-[11px] underline hover:no-underline"
        >
          Retry
        </button>
      )}
    </div>
  )
}
